<template>
  <div>
    <h1 class="heading-1 mb-16">
      {{ i18n(translations.title) }}
    </h1>

    <div v-if="!hasKybVerified">
      <FormattedMessage
        class="mb-28"
        :definition="translations.legalText"
        tag="p"
      >
        <template #link>
          <RevLink target="_blank" :to="HYPERWALLET_LEGAL_LINK">
            {{ i18n(translations.legalCta) }}
          </RevLink>
        </template>
      </FormattedMessage>

      <RevCard class="mx-0 mb-28 p-24 md:p-28">
        <div id="hyperwalletKybUI" />
      </RevCard>
      <ModalOnboarding
        :button-label="i18n(translations.onboardingModalButtonLabel)"
        :title="i18n(translations.onboardingModalTitle)"
      >
        <template #description>
          {{ i18n(translations.onboardingModalDescription) }}
        </template>
        <template #to-do>
          {{ i18n(translations.onboardingModalToDoSection) }}
        </template>
        <template #done>
          {{ i18n(translations.onboardingModalDoneSection) }}
        </template>
      </ModalOnboarding>
    </div>

    <div v-else>
      {{ i18n(translations.alreadyConfigured) }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { navigateTo, useHead } from '#imports'
import { computed, onMounted, onUnmounted, ref } from 'vue'

import {
  getHyperwalletAccessToken,
  getHyperwalletKybUrl,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/hyperwallet'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevCard } from '@ds/components/Card'
import { RevLink } from '@ds/components/Link'

import { ROUTE_NAMES } from '~/constants/routes'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import { HYPERWALLET_LEGAL_LINK } from './Kyb.constants'
import translations from './Kyb.translations'

const i18n = useI18n()
const { data: kybHyperwalletPayload } = await useHttpFetch(getHyperwalletKybUrl)

const { hasKybVerified } = useSellerInfoStore()
const kybScriptLink = computed(() => kybHyperwalletPayload.value?.scriptLink)
const timeout = ref(null as ReturnType<typeof setTimeout> | null)
const { openErrorToast } = useTheToast()

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    HWWidgets: any
  }
}

useHead({
  title: i18n(translations.title),
  script: kybScriptLink.value ? [{ src: kybScriptLink.value.href }] : [],
})

function setupHyperwallet() {
  window.HWWidgets?.events.on('widget:users:submitted', () => {
    const replaceContent = () => {
      const button = document.querySelector('[id^=terminal-page] .btn')
      const sentence = document.querySelector(
        '[id^=terminal-page] .hw-field-message',
      )
      const title = document.querySelector(
        '[id^=terminal-page] .hw-field-title-header',
      )

      if (button) {
        button.innerHTML = i18n(translations.confirmationCta)
      }
      if (sentence) {
        sentence.innerHTML = i18n(translations.confirmationText)
      }
      if (title) {
        title.innerHTML = i18n(translations.confirmationTitle)
      }
    }

    setTimeout(replaceContent, 50)
  })

  window.HWWidgets.events.on('widget:users:completed', () => {
    // Hyperwallet doesn't provide a way to reset the HWWidgets instance.
    // Since the transfer methods is also using the Hyperwallet script
    // the instance wouldn't be properly destroyed with a simple router push.
    // To do so, we need to reload the page, which can be done by going
    // to the page as if it was an external one
    window.location.assign(
      `${window.location.origin}/bo-seller/options/payment/hyperwallet/transfer-methods`,
    )
  })

  window.HWWidgets?.initialize(
    (
      onSuccess: (token: string | undefined) => void,
      onFailure: (error: Error) => void,
    ) => {
      $httpFetch(getHyperwalletAccessToken)
        .then(({ accessToken }) => {
          onSuccess(accessToken)
        })
        .catch((err) => onFailure(err))
    },
  )

  window.HWWidgets?.users
    .configure({
      template: 'plain',
      el: document.getElementById('hyperwalletKybUI'),
    })
    .display()
}

function checkHyperwalletAvailability() {
  if (window.HWWidgets) {
    setupHyperwallet()
  } else {
    timeout.value = setTimeout(checkHyperwalletAvailability, 500)
  }
}

// clear au unmount
onUnmounted(() => {
  if (timeout.value) {
    clearTimeout(timeout.value)
  }
})

onMounted(async () => {
  if (!hasKybVerified) {
    // For now, we got issues with IT accounts on HW, the drop-in-UI request fails : there is no script link to handle
    if (!kybScriptLink.value) {
      openErrorToast({
        content: i18n(translations.hWErrorToastContent),
      })

      // As we cannot display the HW page, we go back to home, the error toasts is still visible though
      await navigateTo({ name: ROUTE_NAMES.ONBOARDING.HOME })
    } else if (kybScriptLink.value) {
      // We need to check if the Hyperwallet external script has run completely before setting up dropin UI
      checkHyperwalletAvailability()
    }
  }
})
</script>

<style lang="css">
/*
*  Hyperwallet sends us there own HTML, we need to use plain
*  css to target tags and selectors to apply our BM styles
*/
#hyperwalletKybUI {
  font-family: BodyFont, sans-serif;
  font-weight: normal;
  color: #111111;
}

/* BREADCRUMB */
#hyperwalletKybUI .hw-breadcrumb-headers {
  font-size: 16px;
  line-height: 19px;
  margin-bottom: 16px;
}

#hyperwalletKybUI .hw-breadcrumb-items {
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 16px;
}

#hyperwalletKybUI .hw-breadcrumb-link-icons {
  display: flex;
}

#hyperwalletKybUI .hw-breadcrumb-links {
  align-self: flex-start;
  text-align: left;
  background-color: transparent;
  text-decoration: underline;
  font-size: 14px;
  line-height: 20px;
  color: #555;
}

#hyperwalletKybUI .hw-breadcrumb-links:hover {
  cursor: pointer;
  text-decoration: none;
}

#hyperwalletKybUI h3.hw-field-headers,
#hyperwalletKybUI h3.hw-table-headers {
  font-size: 22px;
  line-height: 32px;
  margin-bottom: 8px;
}

#hyperwalletKybUI p {
  font-size: 16px;
  line-height: 19px;
  margin-bottom: 8px;
}

/* FORM */
#hyperwalletKybUI .hw-field-groups .hw-group-with-template {
  margin-top: 32px;
}

/* FORM FIELDS */
#hyperwalletKybUI .hw-fields {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

#hyperwalletKybUI .hw-fields:not(.hw-field-document-upload) label {
  color: inherit;
  margin-bottom: 8px;
}

#hyperwalletKybUI .hw-fields input {
  height: 100%;
  width: 100%;
  padding: 14px 16px 14px 12px;
  font: inherit;
  color: inherit;
  background-color: inherit;
  outline: none;
  color: inherit;
  height: 48px;
  border-radius: 6px;
  border: 1px solid #e2e2e2;
  background-color: #fff;
}

#hyperwalletKybUI .hw-fields input:-moz-ui-invalid {
  box-shadow: none;
}

#hyperwalletKybUI .hw-fields input:focus {
  padding-right: 8px;
  color: #555555;
}

/* Get rid of the light blue background color when autofill is used in Chrome. */
#hyperwalletKybUI .hw-fields input:-webkit-autofill,
#hyperwalletKybUI .hw-fields input:-webkit-autofill:hover,
#hyperwalletKybUI .hw-fields input:-webkit-autofill:focus,
#hyperwalletKybUI .hw-fields input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0px 1000px #fff inset !important;
}

/* SELECT */
#hyperwalletKybUI .hw-fields .hw-date-container {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

#hyperwalletKybUI .hw-fields select {
  width: 100%;
  height: 48px;
  position: relative;
  padding: 14px 16px 14px 12px;
  outline: none;
  border-radius: 6px;
  border: 1px solid #e2e2e2;
  margin-bottom: 16px;
}

/* ERRORS */
#hyperwalletKybUI .has-error .hw-error-message {
  color: hsla(358, 84%, 36%, 1);
}

/* BUTTONS */
#hyperwalletKybUI .hw-field-buttons,
#hyperwalletKybUI .hw-field-document-upload,
#hyperwalletKybUI .hw-table-buttons,
#hyperwalletKybUI .hw-tablelist-add-button {
  -webkit-appearance: none;
  border: none;
  text-decoration: none;
  border-radius: 6px;
  cursor: pointer;
  padding: 16px 24px;
  margin-top: 16px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-weight: 500;
  line-height: 20px;
  font-size: 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

#hyperwalletKybUI .hw-field-buttons:disabled,
#hyperwalletKybUI .hw-table-buttons:disabled,
#hyperwalletKybUI .hw-tablelist-add-button:disabled {
  cursor: not-allowed;
}

/* BUTTON PRIMARY */
#hyperwalletKybUI .hw-field-buttons {
  background-color: #111;
  color: #f9f9f9;
}

#hyperwalletKybUI .hw-field-buttons:hover {
  text-decoration: none;
  background: #535353;
}

#hyperwalletKybUI .hw-field-buttons:disabled {
  background: #f1f1f1;
  color: #989898;
}

#hyperwalletKybUI .hw-field-buttons:focus {
  outline: none;
  background: #535353;
}

#hyperwalletKybUI .hw-field-buttons::-moz-focus-inner,
#hyperwalletKybUI .hw-field-document-upload::-moz-focus-inner {
  border: none;
}

/* BUTTON PRIMARY TINY */
#hyperwalletKybUI .hw-table-buttons {
  background-color: #f1f1f1;
  color: #111;
  padding: 8px 12px;
  margin-top: 0;
  line-height: 18px;
  font-size: 14px;
}

#hyperwalletKybUI .hw-table-buttons:disabled {
  background-color: transparent;
  color: #999;
}

#hyperwalletKybUI .hw-table-buttons:focus {
  background-color: #e2e2e2;
}

/* BUTTON SECONDARY */
#hyperwalletKybUI .hw-tablelist-add-button,
#hyperwalletKybUI .hw-field-document-upload {
  background-color: transparent;
  color: #111;
  border: 1px solid #999;
}

#hyperwalletKybUI .hw-tablelist-add-button:hover,
#hyperwalletKybUI .hw-tablelist-add-button:focus {
  background-color: #f1f1f1;
}

#hyperwalletKybUI .hw-tablelist-add-button:focus,
#hyperwalletKybUI .hw-field-document-upload:focus {
  border-color: #535353;
}

#hyperwalletKybUI .hw-tablelist-add-button:disabled,
#hyperwalletKybUI .hw-field-document-upload:disabled {
  color: #999;
}

/* TABLE */
#hyperwalletKybUI table {
  border: 0;
  width: 100%;
  margin: 16px 0 0 0;
  padding: 0;
  border-collapse: collapse;
  border-spacing: 0;
}

#hyperwalletKybUI table tr {
  border-bottom: 1px solid #e5e5e5;
}

#hyperwalletKybUI table tbody tr:last-child {
  border-bottom: 0;
}

#hyperwalletKybUI table th,
#hyperwalletKybUI table td {
  padding: 12px 16px;
  text-align: left;
}

@media only screen and (max-width: 768px) {
  #hyperwalletKybUI table thead {
    display: none;
  }

  #hyperwalletKybUI table tr {
    display: block;
  }

  #hyperwalletKybUI table tr:not(:last-child) {
    margin-bottom: 56px;
  }

  #hyperwalletKybUI table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e5e5e5;
  }

  #hyperwalletKybUI table td:last-child {
    border-bottom: 0;
  }

  #hyperwalletKybUI table td::before {
    font-weight: 500;
  }
}
</style>
